import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { NamedLink } from '../../components';
import { getAudience, DEALER } from '../audience';
import { istGemerkt, merkAenderung, nochOffen } from '../merkliste';
import { dealChance } from '../dealChance';
import DealRing from './DealRing';

import css from './HaendlerBox.module.css';

/**
 * FairwayBid – der Kasten für Anbieter an einer Anfrage
 *
 * Zwei Dinge, die ein Anbieter an genau dieser Stelle braucht und die es
 * bisher nicht gab:
 *
 *   Die Dealchance. Er sieht zwanzig offene Anfragen und hat Zeit für drei
 *   Angebote. Welche drei, stand bisher nirgends – obwohl beide Seiten die
 *   Angaben dafür längst gemacht haben.
 *
 *   Die Merkliste. Nicht jede Anfrage lässt sich sofort rechnen. Ohne
 *   Merkliste bleibt nur der offene Tab, und der überlebt den Freitagabend
 *   nicht.
 *
 * Der Kasten erscheint nur bei Anbietern und nur an fremden, offenen
 * Anfragen. Einer Anlage sagt eine Dealchance nichts, und an einer
 * abgelaufenen Anfrage wäre der Merkknopf ein Versprechen, das die Liste
 * gleich wieder einlöst, indem sie den Eintrag entfernt.
 */
const HaendlerBox = ({ listing, currentUser, className }) => {
  const dispatch = useDispatch();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(false);
  const [offen, setOffen] = useState(false);

  const istHaendler = getAudience(currentUser) === DEALER;
  const eigene = currentUser?.id?.uuid && currentUser.id.uuid === listing?.author?.id?.uuid;

  if (!istHaendler || eigene || !listing?.id?.uuid || !nochOffen(listing)) return null;

  const gemerkt = istGemerkt(currentUser, listing);
  const chance = dealChance(currentUser, listing);

  const merken = () => {
    setLaeuft(true);
    setFehler(false);
    dispatch(updateProfile(merkAenderung(currentUser, listing)))
      .then(antwort => {
        // Der Thunk meldet Fehler als abgelehnten Wert, nicht als Ausnahme.
        if (antwort?.error) setFehler(true);
      })
      .catch(() => setFehler(true))
      .finally(() => setLaeuft(false));
  };

  return (
    <section className={classNames(css.box, className)}>
      {chance ? (
        <>
          <div className={css.kopf}>
            <DealRing wert={chance.wert} stufe={chance.stufe.name} />
            <div className={css.kopfText}>
              <h3 className={css.titel}>Dealchance</h3>
              <p className={css.stufe}>{chance.stufe.text}</p>
              <p className={css.erklaerung}>
                Abgleich Ihrer Profilangaben mit dieser Anfrage. Keine Vorhersage – wie viele
                Anbieter mitbieten, weiß niemand.
              </p>
            </div>
          </div>

          <button
            type="button"
            className={css.aufklappen}
            onClick={() => setOffen(o => !o)}
            aria-expanded={offen}
          >
            {offen ? 'Begründung ausblenden' : 'Woraus ergibt sich das?'}
          </button>

          {offen ? (
            <ul className={css.gruende}>
              {chance.gruende.map(g => (
                <li
                  key={g.text}
                  className={classNames(css.grund, {
                    [css.grundGut]: g.gut === true,
                    [css.grundSchlecht]: g.gut === false,
                  })}
                >
                  {g.text}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <p className={css.ohneProfil}>
          <strong>Dealchance noch nicht berechenbar.</strong> Sobald in Ihrem Profil steht, welche
          Kategorien Sie führen und wohin Sie liefern, zeigen wir hier, wie gut eine Anfrage zu
          Ihnen passt.{' '}
          <NamedLink name="ProfileSettingsPage" className={css.profilLink}>
            Profil ergänzen
          </NamedLink>
        </p>
      )}

      <button
        type="button"
        className={classNames(css.merken, { [css.merkenAktiv]: gemerkt })}
        onClick={merken}
        disabled={laeuft}
        aria-pressed={gemerkt}
      >
        <span className={css.merkStern} aria-hidden="true">
          {gemerkt ? '★' : '☆'}
        </span>
        {laeuft ? 'Einen Moment …' : gemerkt ? 'Auf Ihrer Merkliste' : 'Auf die Merkliste'}
      </button>

      {gemerkt && !laeuft ? (
        <p className={css.merkHinweis}>
          Steht oben im Menü unter{' '}
          <NamedLink name="MerklistePage" className={css.profilLink}>
            Merkliste
          </NamedLink>
          , bis die Frist abläuft oder die Anfrage vergeben ist.
        </p>
      ) : null}

      {fehler ? (
        <p className={css.merkFehler}>
          Das hat nicht geklappt. Bitte noch einmal – Ihre Merkliste ist unverändert.
        </p>
      ) : null}
    </section>
  );
};

export default HaendlerBox;
